import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, PenLine, Lightbulb, Wand2, Copy, FilePlus2, Loader2, ArrowRightCircle } from "lucide-react";

const MODES = [
    { key: "generate", label: "Write a post", icon: PenLine, hint: "Give a topic — get a full draft" },
    { key: "improve", label: "Improve my draft", icon: Wand2, hint: "Paste your text — I'll polish it" },
    { key: "ideas", label: "Get ideas", icon: Lightbulb, hint: "Get fresh post ideas on a theme" },
];

export default function BlogWriter() {
    const navigate = useNavigate();
    const [mode, setMode] = useState("generate");
    const [topic, setTopic] = useState("");
    const [draft, setDraft] = useState("");
    const [tone, setTone] = useState("warm and professional");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null); // {title, body_html} | {text}

    const run = async () => {
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post("/admin/ai/blog-assist", { mode, topic, draft, tone });
            setResult(data);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setBusy(false);
        }
    };

    const saveDraft = async () => {
        try {
            await api.post("/admin/blog", {
                title: result.title || "Untitled draft",
                date: new Date().toISOString().slice(0, 10),
                body_html: result.body_html || "",
                excerpt: "",
                published: false,
            });
            toast.success("Saved to Blog as a draft");
            navigate("/admin/blog");
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const copyAll = () => {
        const text = result.text || `${result.title}\n\n${result.body_html}`;
        navigator.clipboard?.writeText(text);
        toast.success("Copied");
    };

    const sendToEditor = () => {
        navigate("/admin/compose", {
            state: { aiDraft: { title: result.title || "", body_html: result.body_html || "" } },
        });
        toast.success("Sent to the blog editor");
    };

    return (
        <div data-testid="admin-ai-writer">
            <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-b from-[#1e3a8a] to-[#0a1326] ring-1 ring-blue-400/40">
                    <Sparkles className="h-5 w-5 text-sky-300" />
                </span>
                <div>
                    <h1 className="font-serif text-3xl text-blue-50">AI Blog Writer</h1>
                    <p className="text-xs text-slate-300/50">Draft, polish, and brainstorm posts with AI</p>
                </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {MODES.map((m) => (
                    <button
                        key={m.key}
                        data-testid={`ai-mode-${m.key}`}
                        onClick={() => { setMode(m.key); setResult(null); }}
                        className={`rounded-2xl border p-4 text-left transition-colors ${
                            mode === m.key
                                ? "border-blue-400/60 bg-blue-500/10"
                                : "border-blue-500/15 bg-[#0c172e] hover:border-blue-400/30"
                        }`}
                    >
                        <m.icon className={`h-5 w-5 ${mode === m.key ? "text-sky-300" : "text-slate-300/60"}`} />
                        <p className="mt-2 text-sm font-semibold text-blue-50">{m.label}</p>
                        <p className="text-xs text-slate-300/50">{m.hint}</p>
                    </button>
                ))}
            </div>

            <div className="mt-6 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-6">
                {mode === "improve" ? (
                    <textarea
                        data-testid="ai-draft-input"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={7}
                        placeholder="Paste the blog text you'd like me to improve…"
                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] p-3 text-sm text-blue-50 placeholder:text-slate-400/50 focus:border-blue-400 focus:outline-none"
                    />
                ) : (
                    <input
                        data-testid="ai-topic-input"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder={mode === "ideas" ? "A theme, e.g. 'back to school anxiety'" : "What should the post be about?"}
                        className="w-full rounded-xl border border-blue-500/20 bg-[#070e1c] p-3 text-sm text-blue-50 placeholder:text-slate-400/50 focus:border-blue-400 focus:outline-none"
                    />
                )}

                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <label className="text-xs text-slate-300/60">Tone</label>
                    <select
                        data-testid="ai-tone-select"
                        value={tone}
                        onChange={(e) => setTone(e.target.value)}
                        className="rounded-lg border border-blue-500/20 bg-[#070e1c] px-3 py-2 text-sm text-blue-50 focus:border-blue-400 focus:outline-none"
                    >
                        <option>warm and professional</option>
                        <option>warm and playful</option>
                        <option>calm and reassuring</option>
                        <option>clinical and informative</option>
                    </select>
                    <button
                        data-testid="ai-run-button"
                        onClick={run}
                        disabled={busy}
                        className="ml-auto inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-6 py-2.5 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-60"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        {busy ? "Writing…" : "Generate"}
                    </button>
                </div>
            </div>

            {result && (
                <div data-testid="ai-result" className="mt-6 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-6">
                    {result.text ? (
                        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-200/85">{result.text}</pre>
                    ) : (
                        <>
                            {result.title && <h2 className="font-serif text-2xl text-blue-50">{result.title}</h2>}
                            <div
                                className="prose-editor mt-4 max-w-none text-sm leading-relaxed text-slate-200/85"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.body_html || "", { ADD_ATTR: ["target"] }) }}
                            />
                        </>
                    )}
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            data-testid="ai-copy-button"
                            onClick={copyAll}
                            className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 px-5 py-2.5 text-sm text-sky-200 hover:bg-blue-500/10"
                        >
                            <Copy className="h-4 w-4" /> Copy
                        </button>
                        {!result.text && (
                            <button
                                data-testid="ai-sendtoeditor-button"
                                onClick={sendToEditor}
                                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-5 py-2.5 text-sm font-semibold text-white hover:brightness-110"
                            >
                                <ArrowRightCircle className="h-4 w-4" /> Send to blog editor
                            </button>
                        )}
                        {!result.text && (
                            <button
                                data-testid="ai-savedraft-button"
                                onClick={saveDraft}
                                className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 px-5 py-2.5 text-sm text-sky-200 hover:bg-blue-500/10"
                            >
                                <FilePlus2 className="h-4 w-4" /> Save to Blog as draft
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
